/**
 * The job card.
 *
 * The screen the whole product argues for: quoted against actual, in the hand
 * of the person spending the hours, while they are still on site. If a guard
 * is up it is the first thing on the page — a warning below the fold is a
 * warning nobody read.
 *
 * It reads from the device, so it opens in a comms room. The margin shown is
 * recomputed from what the phone is holding rather than read off the row, so a
 * part added two minutes ago is in it — see `repo.job`. Arriving, adding a
 * part and booking time all go through `actions`, which tries the server and
 * falls back to the queue.
 */
import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import * as actions from "../lib/actions";
import { ApiError, api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { returnReasonLabel } from "../lib/daywork";
import { day, hours, marginTone, money, percent, qty, time, titleCase } from "../lib/format";
import { readJobExtras } from "../lib/jobextras";
import {
  PRICE_NEEDS_SIGNAL,
  dollarsFromMarkup,
  marginHint,
  parseDollars,
  setLinePrice,
  MAX_SELL_CENTS,
} from "../lib/lineprice";
import { useLocal } from "../lib/local";
import { navigationLinks, siteAddress } from "../lib/navigate";
import * as repo from "../lib/repo";
import { bookingError, durationLabel, newestFirst, parseMinutes } from "../lib/timebook";
import { AddPart } from "../components/AddPart";
import { BillingCard } from "../components/BillingCard";
import { OnSiteClock } from "../components/OnSiteClock";
import { InstallRegister } from "../components/InstallRegister";
import { TestSheets } from "../components/TestSheets";
import { JobWriteUp } from "../components/JobWriteUp";
import { Card, Chip, DataTable, Loading, PageHeader, type Tone } from "../components/ui";
import type { CostingOut, Guard, JobOut, ReturnReason } from "../lib/types";

const GUARD_COPY: Record<Exclude<Guard, "none">, { title: string; tone: Tone }> = {
  unapproved_variation: {
    title: "A variation is waiting on approval",
    tone: "warn",
  },
  over_quoted_hours: {
    title: "Past the quoted hours",
    tone: "warn",
  },
  below_break_even: {
    title: "Below break-even",
    tone: "crit",
  },
};

/** Statuses a tech can finish a job from. Unscheduled work was never started; the rest are already past it. */
const FINISHABLE = ["scheduled", "dispatched", "on_site", "in_progress", "awaiting_parts", "awaiting_approval"];

const MOVE_NEEDS_SIGNAL = "Moving a line needs a signal, because it moves the van's stock.";

export default function JobDetail() {
  const { id = "" } = useParams();
  const { can, session } = useAuth();
  // A technician records what happened; calling the work off is the office's
  // decision and the server refuses it from anyone else anyway.
  const canCancel = can("owner", "office", "supervisor");
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Local first; a job this device doesn't carry is fetched and kept.
  const { data: job, isPending, reload } = useLocal(async () => (await repo.job(id)) ?? repo.fetchJob(id), [id]);

  // When it was finished and where it is, which `repo.job` doesn't carry.
  // Re-read when the job changes: on a job the device had never held, the
  // first read ran before the job arrived, so the address and "finished on"
  // only appeared on a second visit.
  const { data: extras } = useLocal(() => readJobExtras(id), [id, job?.updated_at, job?.status]);

  // Finishing the job says what that means for billing, up by the buttons it
  // came from rather than a screen away.
  const [doneSaid, setDoneSaid] = useState<{ tone: "info" | "warn" | "crit"; text: string } | null>(null);
  const doneNote = useRef<HTMLParagraphElement | null>(null);
  useEffect(() => {
    doneNote.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [doneSaid]);

  async function finish() {
    if (!window.confirm("Mark this job as finished?")) return;
    setBusy(true);
    setDoneSaid(null);
    try {
      const { result, queued, message } = await actions.completeJob(id);
      const completion = result?.completion;
      if (queued) {
        setDoneSaid({ tone: "info", text: message ?? "Finished on this phone. It'll send when you're back in range." });
      } else if (completion?.needs_invoice) {
        const about =
          completion.draft_subtotal_cents === null ? "" : ` — about ${money(completion.draft_subtotal_cents)} ex GST`;
        setDoneSaid({ tone: "info", text: `Finished. Ready for the office to invoice${about}.` });
      } else if (completion) {
        const reason = completion.reason?.trim();
        setDoneSaid({
          tone: reason && /nothing|no billable|not billable/i.test(reason) ? "warn" : "info",
          text: reason || "Finished.",
        });
      } else {
        setDoneSaid({ tone: "info", text: "Finished." });
      }
      reload();
    } catch (err) {
      setDoneSaid({ tone: "crit", text: err instanceof ApiError ? err.message : "Couldn't mark it finished." });
    } finally {
      setBusy(false);
    }
  }

  // Off site for today and coming back: not "finished", so the job stays open
  // and lands on the office's "to book" list.
  const [leaving, setLeaving] = useState(false);

  async function leave(plan: { reason: ReturnReason; note: string; hours: number | null }) {
    if (!session) {
      setDoneSaid({ tone: "crit", text: "Sign in again to leave site." });
      return;
    }
    setBusy(true);
    setDoneSaid(null);
    try {
      const { queued, message } = await actions.leaveSite(id, session.userId, plan);
      setDoneSaid({
        tone: "info",
        text: queued
          ? (message ?? "Off site. It'll send when you're back in range.")
          : "Off site. The office will book the next visit.",
      });
      setLeaving(false);
      reload();
    } catch (err) {
      setDoneSaid({ tone: "crit", text: err instanceof ApiError ? err.message : "Couldn't record leaving site." });
    } finally {
      setBusy(false);
    }
  }

  const [editing, setEditing] = useState<{ lineId: string; value: string } | null>(null);
  // The charge editor: dollars and markup are two ways to say one number.
  const [pricing, setPricing] = useState<{ lineId: string; dollars: string; markup: string } | null>(null);
  // Opened near the bottom of a long job, the editor landed under the tab bar.
  useEffect(() => {
    if (pricing) document.querySelector(".price-row")?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [pricing?.lineId]);

  function openPrice(line: { id: string; unit_sell_cents: number }) {
    setLineSaid(null);
    setEditing(null);
    setMoving(null);
    setPricing({ lineId: line.id, dollars: (line.unit_sell_cents / 100).toFixed(2), markup: "" });
  }

  async function savePrice(line: { id: string }) {
    setLineSaid(null);
    const next = pricing ? parseDollars(pricing.dollars) : null;
    if (!pricing || next === null) {
      setLineSaid({
        tone: "crit",
        text: `A charge has to be a number from $0 to ${money(MAX_SELL_CENTS).replace(".00", "")}.`,
      });
      return;
    }
    setBusy(true);
    try {
      await setLinePrice(id, line.id, next);
      setPricing(null);
      reload();
    } catch (err) {
      setLineSaid({
        tone: "crit",
        text: err instanceof ApiError && err.status !== 0 ? err.message : PRICE_NEEDS_SIGNAL,
      });
    } finally {
      setBusy(false);
    }
  }
  // Said beside the table it is about. These used to go to the notice at the top
  // of the page, which on a phone is a screen above where the tech is looking:
  // a failed save looked like nothing happening (D80).
  const [lineSaid, setLineSaid] = useState<{ tone: "info" | "crit" | "warn"; text: string } | null>(null);
  const lineNote = useRef<HTMLParagraphElement | null>(null);
  useEffect(() => {
    lineNote.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [lineSaid]);

  // The Time card says its own things, in the same way and for the same reason.
  const [timeSaid, setTimeSaid] = useState<{ tone: "info" | "warn" | "crit"; text: string } | null>(null);
  const timeNote = useRef<HTMLParagraphElement | null>(null);
  useEffect(() => {
    timeNote.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [timeSaid]);
  const [customMinutes, setCustomMinutes] = useState("");
  const [timeBillable, setTimeBillable] = useState(true);

  // Moving a line to another job of the same customer. The candidates are
  // fetched when the picker opens, not read from the device: a move is online
  // only, and the device's copy of the customer's other jobs may be stale or
  // partial (the working set carries only this tech's own work, D16).
  const [moving, setMoving] = useState<{ lineId: string; to: string } | null>(null);
  const [moveTargets, setMoveTargets] = useState<JobOut[] | null>(null);

  async function openMove(lineId: string) {
    setLineSaid(null);
    setEditing(null);
    setPricing(null);
    setMoveTargets(null);
    setMoving({ lineId, to: "" });
    try {
      const all = await api.jobs.list({});
      const here = job;
      const others = all
        .filter(
          (j) =>
            j.customer_id === here?.customer_id && j.id !== id && j.status !== "cancelled" && j.status !== "invoiced",
        )
        .sort((a, b) => (b.scheduled_start ?? b.updated_at).localeCompare(a.scheduled_start ?? a.updated_at));
      setMoveTargets(others);
    } catch {
      setMoving(null);
      setLineSaid({ tone: "crit", text: MOVE_NEEDS_SIGNAL });
    }
  }

  /**
   * Moving a line to another job. The part goes back on the van and is taken
   * again for the new job, so this is online only — the same reason a quantity
   * change is (D70).
   */
  async function saveMove(lineId: string) {
    if (!moving || moving.lineId !== lineId || !moving.to) return;
    setBusy(true);
    setLineSaid(null);
    try {
      const result = await actions.moveLine(id, lineId, moving.to);
      const number = result?.number ?? moveTargets?.find((j) => j.id === moving.to)?.number ?? "the other job";
      setMoving(null);
      const warning = result?.stock?.warnings?.[0];
      setLineSaid(
        warning
          ? { tone: "warn", text: `Moved to ${number}. ${warning}` }
          : { tone: "info", text: `Moved to ${number}.` },
      );
      reload();
    } catch (err) {
      setLineSaid({
        tone: "crit",
        text: err instanceof ApiError && err.status !== 0 ? err.message : MOVE_NEEDS_SIGNAL,
      });
    } finally {
      setBusy(false);
    }
  }

  /**
   * Changing a quantity. A cut puts the difference back on the van; raising it
   * does not take more, because whoever is editing is not standing at the
   * vehicle (D70) — so a raise says so, rather than leaving the tech to
   * wonder why the van count did not move.
   */
  async function saveQty(line: { id: string; qty: number }) {
    const next = Number(editing?.value);
    setLineSaid(null);
    if (!editing || !Number.isFinite(next) || next <= 0) {
      setLineSaid({ tone: "crit", text: "A quantity has to be a number above zero. To remove it, use Remove." });
      return;
    }
    setBusy(true);
    try {
      const result = await actions.changeQty(id, line.id, next);
      setEditing(null);
      if (next > Number(line.qty)) {
        setLineSaid({
          tone: "info",
          text: "Quantity raised. That doesn't take more off the van — correct the van's count if you fitted extra from stock.",
        });
      } else if (result?.stock?.warnings?.[0]) {
        setLineSaid({ tone: "warn", text: result.stock.warnings[0] });
      }
      reload();
    } catch (err) {
      setLineSaid({
        tone: "crit",
        text:
          err instanceof ApiError && err.status !== 0
            ? err.message
            : "Changing a quantity needs a signal, because it moves the van's stock. Take the line off and add it again, or wait until you're back in range.",
      });
    } finally {
      setBusy(false);
    }
  }

  /**
   * Taking a line back off. The confirmation names the line rather than
   * asking "are you sure?", because the mistake this prevents is removing the
   * wrong row, and a yes/no box does not help with that.
   */
  async function removeLine(lineId: string, description: string) {
    if (!window.confirm(`Take "${description}" off this job? The part goes back on the van it came from.`)) return;
    setBusy(true);
    setLineSaid(null);
    try {
      const { queued, message } = await actions.removeLine(id, lineId);
      if (queued && message) setLineSaid({ tone: "info", text: message });
      reload();
    } catch (err) {
      setLineSaid({ tone: "crit", text: err instanceof ApiError ? err.message : "Couldn't take that line off." });
    } finally {
      setBusy(false);
    }
  }

  /** Book time as whoever is signed in. Nothing is sent for a bad number. */
  async function bookTime(minutes: number | null) {
    if (minutes === null) {
      setTimeSaid({ tone: "crit", text: "Minutes has to be a whole number from 1 to 1440." });
      return;
    }
    if (!session) {
      setTimeSaid({ tone: "crit", text: "Sign in again to book time." });
      return;
    }
    setBusy(true);
    setTimeSaid(null);
    try {
      const { message } = await actions.bookTime(id, {
        minutes,
        user_id: session.userId,
        billable: timeBillable,
      });
      // Time on a finished job is allowed but said, so it reads as a warning.
      if (message) setTimeSaid({ tone: /finished/i.test(message) ? "warn" : "info", text: message });
      setCustomMinutes("");
      reload();
    } catch (err) {
      setTimeSaid({ tone: "crit", text: bookingError(err) });
    } finally {
      setBusy(false);
    }
  }

  /**
   * Calling a job off. The reason is required by the server and asked for
   * here rather than sent empty, because "cancelled" with no reason is the
   * row somebody has to ring three people about six months later.
   */
  async function cancelJob() {
    const reason = window.prompt("Why is this job being cancelled?");
    if (reason === null || !reason.trim()) return;
    setBusy(true);
    setFormError(null);
    try {
      await actions.cancelJob(id, reason.trim());
      setNotice(null);
      reload();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Couldn't cancel it.");
    } finally {
      setBusy(false);
    }
  }

  if (isPending) return <Loading what="Opening the job" />;

  if (!job) {
    // Not on this device. Said plainly rather than as an error, because the
    // usual cause is a job belonging to another tech — the working set carries
    // this technician's own work on purpose (D16).
    return (
      <div className="page">
        <div className="notbuilt">
          <h2>Not on this device</h2>
          <p>
            This job isn't in what your phone is carrying. If you're back in range, sync and try again — otherwise it
            belongs to someone else's run.
          </p>
        </div>
      </div>
    );
  }

  const costing = job.costing;
  const guard = costing?.guard ?? "none";
  // A called-off job takes no new work, and the server says so too — the
  // screen hides the buttons rather than letting someone find out by 409.
  const cancelled = job.status === "cancelled";

  return (
    <div className="page">
      <PageHeader eyebrow={`${job.number} · ${titleCase(job.type)}`} title={job.title}>
        {job.description}
      </PageHeader>

      {cancelled ? (
        <p className="notice notice-warn" role="status">
          This job is cancelled. It keeps its number and everything booked to it — the reason is in the description
          above.
        </p>
      ) : (
        canCancel && (
          <div className="btn-row" style={{ marginBottom: 10 }}>
            <button className="btn" type="button" disabled={busy} onClick={() => void cancelJob()}>
              Cancel this job
            </button>
          </div>
        )
      )}

      {cancelled ? null : job.status === "completed" ? (
        <p className="tiny muted" style={{ marginBottom: 10 }}>
          {extras?.completed_at ? `Finished ${day(extras.completed_at)}` : "Finished"}
        </p>
      ) : (
        FINISHABLE.includes(job.status) && (
          <div className="btn-row" style={{ marginBottom: 10 }}>
            <button
              className="btn btn-primary finish-button"
              type="button"
              disabled={busy}
              onClick={() => void finish()}
            >
              Job finished
            </button>
            {!leaving && (
              <button
                className="btn"
                type="button"
                style={{ minHeight: 48, width: "100%" }}
                disabled={busy}
                onClick={() => {
                  setDoneSaid(null);
                  setLeaving(true);
                }}
              >
                Leaving for today — coming back
              </button>
            )}
          </div>
        )
      )}
      {leaving && !cancelled && FINISHABLE.includes(job.status) && (
        <LeaveForm busy={busy} onSubmit={(plan) => void leave(plan)} onCancel={() => setLeaving(false)} />
      )}
      {doneSaid && (
        <p
          ref={doneNote}
          className={`notice notice-${doneSaid.tone}`}
          role={doneSaid.tone === "crit" ? "alert" : "status"}
        >
          {doneSaid.text}
        </p>
      )}

      <SiteNav site={extras?.site ?? null} />

      {guard !== "none" && costing && <GuardBanner guard={guard} costing={costing} />}

      {formError && (
        <p className="notice notice-crit" role="alert">
          {formError}
        </p>
      )}

      {notice && (
        <p className="notice notice-info" role="status">
          {notice}
        </p>
      )}

      <div className="split">
        <div className="split-main">
          <OnSiteClock job={job} onChanged={reload} />

          <Card
            title="Parts and labour"
            subtitle={`${job.lines.length} line${job.lines.length === 1 ? "" : "s"}`}
            padded={false}
          >
            {job.lines.length === 0 ? (
              <p className="state">
                {job.arrived_at ? "No parts or labour on it yet." : "Nothing booked yet — the tech hasn't arrived."}
              </p>
            ) : (
              <DataTable
                columns={[
                  { key: "d", label: "Line" },
                  { key: "q", label: "Qty" },
                  { key: "c", label: "Cost", align: "right", hideOnPhone: true },
                  { key: "s", label: "Charge", align: "right" },
                  { key: "x", label: "" },
                ]}
              >
                {job.lines.map((line) => (
                  <Fragment key={line.id}>
                    <tr>
                      <td>
                        {line.description}
                        {line.under_warranty && (
                          <>
                            {" "}
                            <Chip tone="warn">warranty</Chip>
                          </>
                        )}
                        {line.serial && <div className="mono tiny muted">SN {line.serial}</div>}
                      </td>
                      <td className="mono">
                        {editing?.lineId === line.id ? (
                          <span className="qty-edit">
                            <input
                              type="number"
                              inputMode="decimal"
                              min="0"
                              step="any"
                              aria-label={`Quantity of ${line.description}`}
                              value={editing.value}
                              onChange={(e) => setEditing({ lineId: line.id, value: e.target.value })}
                              autoFocus
                            />
                            <button
                              className="link-button"
                              type="button"
                              disabled={busy}
                              onClick={() => void saveQty(line)}
                            >
                              Save
                            </button>
                            <button
                              className="link-button"
                              type="button"
                              disabled={busy}
                              onClick={() => {
                                setEditing(null);
                                setLineSaid(null);
                              }}
                            >
                              Cancel
                            </button>
                          </span>
                        ) : cancelled ? (
                          qty(line.qty)
                        ) : (
                          <button
                            className="link-button qty-button"
                            type="button"
                            disabled={busy}
                            aria-label={`Change quantity of ${line.description}`}
                            onClick={() => {
                              setLineSaid(null);
                              setMoving(null);
                              setPricing(null);
                              setEditing({ lineId: line.id, value: String(line.qty) });
                            }}
                          >
                            {qty(line.qty)}
                          </button>
                        )}
                      </td>
                      <td className="right mono muted phone-hide">{money(line.unit_cost_cents)}</td>
                      <td className="right mono">
                        {line.under_warranty || !line.billable ? (
                          "—"
                        ) : cancelled ? (
                          money(line.unit_sell_cents)
                        ) : (
                          <button
                            className="link-button qty-button price-button"
                            type="button"
                            disabled={busy}
                            aria-label={`Change charge of ${line.description}`}
                            onClick={() => openPrice(line)}
                          >
                            {money(line.unit_sell_cents)}
                          </button>
                        )}
                      </td>
                      <td className="right">
                        {!cancelled && moving?.lineId !== line.id && (
                          <>
                            <button
                              className="link-button row-action"
                              type="button"
                              disabled={busy}
                              aria-label={`Move ${line.description} to another job`}
                              onClick={() => void openMove(line.id)}
                            >
                              Move
                            </button>{" "}
                            <button
                              className="link-button row-action"
                              type="button"
                              disabled={busy}
                              onClick={() => void removeLine(line.id, line.description)}
                            >
                              Remove
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                    {!cancelled && pricing?.lineId === line.id && (
                      <tr className="move-row price-row">
                        <td colSpan={5}>
                          <PriceEditor
                            description={line.description}
                            unitCostCents={line.unit_cost_cents}
                            value={pricing}
                            busy={busy}
                            onChange={setPricing}
                            onSave={() => void savePrice(line)}
                            onCancel={() => {
                              setPricing(null);
                              setLineSaid(null);
                            }}
                          />
                        </td>
                      </tr>
                    )}
                    {!cancelled && moving?.lineId === line.id && (
                      <tr className="move-row">
                        <td colSpan={5}>
                          <span className="move-edit">
                            {moveTargets === null ? (
                              <span className="muted">Finding jobs…</span>
                            ) : moveTargets.length === 0 ? (
                              <span className="muted">No other open jobs for this customer.</span>
                            ) : (
                              <select
                                aria-label={`Move ${line.description} to`}
                                value={moving.to}
                                disabled={busy}
                                onChange={(e) => setMoving({ lineId: line.id, to: e.target.value })}
                              >
                                <option value="">Move to…</option>
                                {moveTargets.map((j) => (
                                  <option key={j.id} value={j.id}>
                                    {`${j.number} — ${j.title}`}
                                  </option>
                                ))}
                              </select>
                            )}
                            <button
                              className="link-button"
                              type="button"
                              disabled={busy || !moving.to}
                              onClick={() => void saveMove(line.id)}
                            >
                              Move it
                            </button>
                            <button
                              className="link-button"
                              type="button"
                              disabled={busy}
                              onClick={() => {
                                setMoving(null);
                                setLineSaid(null);
                              }}
                            >
                              Cancel
                            </button>
                          </span>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </DataTable>
            )}
            {lineSaid && (
              <p
                ref={lineNote}
                className={`notice notice-${lineSaid.tone}`}
                role={lineSaid.tone === "crit" ? "alert" : "status"}
                style={{ margin: "8px 14px" }}
              >
                {lineSaid.text}
              </p>
            )}
            {!cancelled && (
              <AddPart jobId={job.id} onAdded={reload} fixedPrice={job.quoted_cents > 0} onSaid={setLineSaid} />
            )}
          </Card>

          {!cancelled && <BillingCard job={job} onChanged={reload} />}

          {!cancelled && (
            <Card
              title="Time"
              subtitle={durationLabel(
                (job.time_entries ?? []).filter((t) => t.kind !== "break").reduce((sum, t) => sum + t.minutes, 0),
              )}
            >
              {(job.time_entries ?? []).length === 0 ? (
                <p className="tiny muted">No time booked yet.</p>
              ) : (
                <div className="stack">
                  {newestFirst(job.time_entries ?? []).map((entry) => (
                    <div key={entry.id} className="doc-row">
                      <b>{durationLabel(entry.minutes)}</b>
                      <span className="tiny muted">{day(entry.started_at)}</span>
                      {entry.kind === "break" && <Chip tone="neutral">break</Chip>}
                      {!entry.ended_at && (entry.kind === "break" || entry.kind === "on_site") && (
                        <Chip tone="info">running</Chip>
                      )}
                      {entry.billable === false && <Chip tone="neutral">non-billable</Chip>}
                    </div>
                  ))}
                </div>
              )}

              <div className="btn-row" style={{ marginTop: 10 }}>
                {[
                  ["+15 min", 15],
                  ["+30 min", 30],
                  ["+1 hr", 60],
                ].map(([label, minutes]) => (
                  <button
                    key={label}
                    type="button"
                    className="btn"
                    style={{ minHeight: 44 }}
                    disabled={busy}
                    onClick={() => void bookTime(Number(minutes))}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="btn-row" style={{ marginTop: 8 }}>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={1440}
                  step={1}
                  value={customMinutes}
                  placeholder="Minutes"
                  aria-label="Minutes to book"
                  onChange={(e) => setCustomMinutes(e.target.value)}
                  style={{ width: 110, minHeight: 44 }}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ minHeight: 44 }}
                  disabled={busy}
                  onClick={() => void bookTime(parseMinutes(customMinutes))}
                >
                  Book
                </button>
              </div>
              <label
                className="tiny"
                style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, minHeight: 44 }}
              >
                <input type="checkbox" checked={timeBillable} onChange={(e) => setTimeBillable(e.target.checked)} />
                Billable
              </label>
              {timeSaid && (
                <p
                  ref={timeNote}
                  className={`notice notice-${timeSaid.tone}`}
                  role={timeSaid.tone === "crit" ? "alert" : "status"}
                  style={{ marginTop: 8 }}
                >
                  {timeSaid.text}
                </p>
              )}
            </Card>
          )}

          <InstallRegister jobId={job.id} />

          <TestSheets jobId={job.id} />

          <JobWriteUp key={`${job.id}-${job.updated_at}`} job={job} onSaved={reload} />

          <Card title="Status">
            <dl className="kv">
              <dt>Status</dt>
              <dd>
                <Chip tone="info">{titleCase(job.status)}</Chip>
              </dd>
              <dt>Scheduled</dt>
              <dd className="mono">
                {job.scheduled_start ? `${time(job.scheduled_start)} – ${time(job.scheduled_end)}` : "Not scheduled"}
              </dd>
              {job.sla_due_at && (
                <>
                  <dt>SLA due</dt>
                  <dd className="mono">{time(job.sla_due_at)}</dd>
                </>
              )}
            </dl>
            {job.needs_return && (
              <p className="notice notice-warn" role="status" style={{ marginBottom: 10 }}>
                <b>Coming back — {returnReasonLabel(job.return_reason)}</b>
                {job.return_note && <span style={{ display: "block" }}>{job.return_note}</span>}
                {job.return_hours ? <span style={{ display: "block" }}>{job.return_hours} h next visit</span> : null}
                {job.left_site_at && (
                  <span style={{ display: "block" }}>
                    left {day(job.left_site_at)}, {time(job.left_site_at)}
                  </span>
                )}
              </p>
            )}
            <div className="btn-row">
              <Link className="btn" to={`/invoices?job=${job.id}`}>
                Invoice this job
              </Link>
            </div>
          </Card>
        </div>

        <aside className="split-side">
          {costing && <MarginCard costing={costing} quoted={job.quoted_cents} mode={job.billing_mode} />}
        </aside>
      </div>
    </div>
  );
}

const LEAVE_REASONS: [ReturnReason, string][] = [
  ["parts", "Parts needed"],
  ["more_time", "More time needed"],
  ["customer", "Customer not available / asked us to stop"],
  ["other", "Other"],
];

/** The few things the office needs to book the next visit; asked inline, not in a prompt. */
function LeaveForm({
  busy,
  onSubmit,
  onCancel,
}: {
  busy: boolean;
  onSubmit: (plan: { reason: ReturnReason; note: string; hours: number | null }) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState<ReturnReason>("parts");
  const [note, setNote] = useState("");
  const [nextHours, setNextHours] = useState("");
  const [error, setError] = useState<string | null>(null);
  const noteRequired = reason === "parts" || reason === "other";

  function submit() {
    const trimmed = note.trim();
    if (noteRequired && !trimmed) {
      setError(reason === "parts" ? "Say which parts, and how many." : "Say what's left to do.");
      return;
    }
    const typed = nextHours.trim();
    const hoursValue = typed === "" ? null : Number(typed);
    if (hoursValue !== null && (!Number.isFinite(hoursValue) || hoursValue < 0.5 || hoursValue > 100)) {
      setError("Hours for the next visit has to be 0.5 or more.");
      return;
    }
    setError(null);
    onSubmit({ reason, note: trimmed, hours: hoursValue });
  }

  return (
    <div className="leave-form">
      <label>
        <span className="tiny muted">Why are you coming back?</span>
        <select value={reason} disabled={busy} onChange={(e) => setReason(e.target.value as ReturnReason)}>
          {LEAVE_REASONS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span className="tiny muted">What's needed ({noteRequired ? "required" : "optional"})</span>
        <textarea
          value={note}
          disabled={busy}
          maxLength={500}
          placeholder={reason === "parts" ? "Which parts, how many" : ""}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      <label>
        <span className="tiny muted">Hours for the next visit (optional)</span>
        <input
          type="number"
          inputMode="decimal"
          min={0.5}
          step={0.5}
          value={nextHours}
          disabled={busy}
          onChange={(e) => setNextHours(e.target.value)}
          style={{ width: 120 }}
        />
      </label>
      {error && (
        <p className="notice notice-crit" role="alert">
          {error}
        </p>
      )}
      <div className="btn-row">
        <button className="btn btn-primary" type="button" style={{ minHeight: 44 }} disabled={busy} onClick={submit}>
          Leave site
        </button>
        <button className="btn" type="button" style={{ minHeight: 44 }} disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function SiteNav({ site }: { site: NonNullable<Awaited<ReturnType<typeof readJobExtras>>["site"]> | null }) {
  const links = navigationLinks(site);
  if (!links) return null;
  return (
    <div className="site-nav">
      <div className="site-nav-where">
        {site?.name && <b>{site.name}</b>}
        <span className="tiny muted">{siteAddress(site) || links.label}</span>
      </div>
      <a className="btn btn-primary nav-button" href={links.google} target="_blank" rel="noopener">
        Navigate
      </a>
      <a className="link-button nav-waze" href={links.waze} target="_blank" rel="noopener">
        Waze
      </a>
    </div>
  );
}

/** Charge each, as dollars or as a markup on cost; typing one rewrites the other. */
function PriceEditor({
  description,
  unitCostCents,
  value,
  busy,
  onChange,
  onSave,
  onCancel,
}: {
  description: string;
  unitCostCents: number;
  value: { lineId: string; dollars: string; markup: string };
  busy: boolean;
  onChange: (next: { lineId: string; dollars: string; markup: string }) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const hint = marginHint(unitCostCents, parseDollars(value.dollars));
  return (
    <div className="price-edit">
      <label>
        <span className="tiny muted">Charge each ($)</span>
        <input
          type="number"
          inputMode="decimal"
          min="0"
          max="999999"
          step="0.01"
          aria-label={`Charge each for ${description}`}
          value={value.dollars}
          onChange={(e) => onChange({ lineId: value.lineId, dollars: e.target.value, markup: "" })}
          autoFocus
        />
      </label>
      <label>
        <span className="tiny muted">Markup %</span>
        <input
          type="number"
          inputMode="decimal"
          min="0"
          step="any"
          aria-label={`Markup percent for ${description}`}
          value={value.markup}
          onChange={(e) => {
            const markup = e.target.value;
            const dollars = dollarsFromMarkup(unitCostCents, markup);
            onChange({ lineId: value.lineId, markup, dollars: dollars ?? value.dollars });
          }}
        />
      </label>
      {hint && <span className="tiny muted price-margin">{hint}</span>}
      <span className="price-actions">
        <button className="link-button" type="button" disabled={busy} onClick={onSave}>
          Save
        </button>
        <button className="link-button" type="button" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </span>
    </div>
  );
}

function GuardBanner({ guard, costing }: { guard: Exclude<Guard, "none">; costing: CostingOut }) {
  const copy = GUARD_COPY[guard];
  return (
    <div className={`guard guard-${copy.tone}`} role="alert">
      <b>{copy.title}</b>
      <ul>
        {costing.warnings.map((w) => (
          <li key={w}>{w}</li>
        ))}
      </ul>
    </div>
  );
}

const MODE_LABEL: Record<string, string> = {
  service_call: "Service call",
  contractor: "Contractor rates",
  time_and_materials: "Time & materials",
};

function MarginCard({ costing, quoted, mode }: { costing: CostingOut; quoted: number; mode?: string }) {
  const tone = marginTone(costing.margin_bp);
  const fixedPrice = quoted > 0;

  return (
    <Card title="Live margin" subtitle={fixedPrice ? "Fixed price" : (MODE_LABEL[mode ?? ""] ?? "Time & materials")}>
      <div className="bar" style={{ marginBottom: 12 }}>
        <i
          className={`bar-fill bar-${tone}`}
          style={{ width: `${Math.max(0, Math.min(100, costing.margin_bp / 100))}%` }}
        />
      </div>

      <dl className="kv kv-money">
        <dt>{fixedPrice ? "Quoted" : "Billable"}</dt>
        <dd className="mono">{money(costing.revenue_cents)}</dd>
        <dt>Cost so far</dt>
        <dd className="mono">{money(costing.cost_cents)}</dd>
        <dt className="total">Margin</dt>
        <dd className={`mono total text-${tone}`}>
          {money(costing.margin_cents)} · {percent(costing.margin_bp)}
        </dd>
      </dl>

      <dl className="kv" style={{ marginTop: 12 }}>
        <dt>Hours</dt>
        <dd className="mono">
          {hours(costing.hours_actual)}
          {/* Against the ceiling, not the bare quote: an approved variation
              lifted it, and nagging about hours that were signed off is how a
              warning stops being read. */}
          {costing.hours_ceiling !== null && ` of ${hours(costing.hours_ceiling)}`}
        </dd>
        {costing.headroom_cents !== null && (
          <>
            <dt>Headroom</dt>
            <dd className={`mono ${costing.headroom_cents < 0 ? "text-crit" : ""}`}>{money(costing.headroom_cents)}</dd>
          </>
        )}
      </dl>

      {fixedPrice && (
        <p className="tiny muted" style={{ marginTop: 10 }}>
          On a fixed-price job the lines track cost — the quote is what bills. An absorbed part eats margin rather than
          inflating the invoice.
        </p>
      )}
    </Card>
  );
}
